// TRAC-1124: on a phone the on-screen keyboard goes away once a message is sent, so the
// full-screen chat gets its whole height back for the reply (Dylan, 1 Oct 2026: "After
// you send a message, the keyboard should disappear, and the chat should take full
// screen again"). On a desktop (tray, bubble, or a narrow window driven by a mouse) the
// box keeps focus so people can type their next message straight away.
//
// Phone detection is the same as the phone CSS block in ChatInterface.module.css (a
// coarse pointer on a narrow screen), plus the loader's report that the keyboard is on
// screen (<html data-traci-kb="up">, see embedKeyboardBridge.ts).
//
// Blurring fires focusout with no relatedTarget, so the keyboard bridge reports the
// focus loss to the loader exactly as a manual close does, and the loader unpins the
// panel early (no website showing above the chat while the keyboard slides away).

import { KB_ATTR } from '../pages/embedKeyboardBridge';

export const PHONE_MEDIA_QUERY = '(max-width: 767px) and (pointer: coarse)';

/**
 * TRAC-1152: fired on the window just before the chat blurs the box itself, so the keyboard ride
 * (embedKeyboardRide.ts) can tell a send's close (the keyboard goes down 100 to 450 ms later) from
 * the keyboard's own Done (it is already going down).
 */
export const KB_APP_BLUR_EVENT = 'traci-app-blur';

/** True on a touch phone, or whenever the loader says the phone keyboard is on screen. */
export function isTouchPhone(win: Window = window): boolean {
  try {
    if (win.document.documentElement.getAttribute(KB_ATTR) === 'up') return true;
    return typeof win.matchMedia === 'function' && win.matchMedia(PHONE_MEDIA_QUERY).matches;
  } catch {
    return false;
  }
}

/**
 * Close the phone keyboard by blurring `el` when it is the focused element on a touch
 * phone. A no-op on desktop or when `el` is not focused. Returns whether it blurred.
 */
export function dismissPhoneKeyboard(el: HTMLElement | null | undefined, win: Window = window): boolean {
  if (!el || el.ownerDocument.activeElement !== el || !isTouchPhone(win)) return false;
  try { win.dispatchEvent(new Event(KB_APP_BLUR_EVENT)); } catch { /* old browser: the ride reads the tap instead */ }
  el.blur();
  return true;
}
